import { useEffect, useRef } from "react";

import { addDays, subMonths } from "date-fns";
import { atom, useAtom, useAtomValue } from "jotai";

import { storeIntegrationTypes } from "src/lib/constants";
import {
  getComparisonDates,
  getDaysBetween,
  getLastMonthsRange,
  getMonthsBetween,
  getWeeksBetween,
} from "src/lib/date";
import { useApiQuery, useSafeWrapper } from "src/lib/hooks/useApi";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { useNumberFormat } from "src/lib/hooks/useNumberFormat";
import { reportingIntervalAtom } from "src/lib/reportingInterval";

// Analytics atoms
const createAnalyticsAtom = (id, additionalFields) => {
  const dateRange = getLastMonthsRange(12);

  const defaultState = {
    id,
    dateFrom: dateRange.from,
    dateTo: dateRange.to,
    dateMin: null,
    dateMax: new Date(),
    comparisonMode: "noComparison",
    loaded: false,
    loading: false,
    data: [],
    ...additionalFields,
  };

  return atom({
    ...defaultState,
    defaultState, // keep a copy of the default state for resets
  });
};

export const dashboardUnitEconomicsAtom = createAnalyticsAtom(
  "dashboardUnitEconomics",
  {
    comparisonMode: "previousPeriod",
  }
);
export const dashboardCohortAtom = createAnalyticsAtom("dashboardCohort", {
  comparisonMode: "previousPeriod",
});
export const unitEconomicsAtom = createAnalyticsAtom("unitEconomics");
export const cohortAtom = createAnalyticsAtom("cohort");
export const normalizedPLAtom = createAnalyticsAtom("normalizedPL");
export const aovAtom = createAnalyticsAtom("aov");
export const breakEvenAtom = createAnalyticsAtom("breakEven", {
  dateFrom: addDays(subMonths(new Date(), 3), 1),
});
export const marketingActivityAtom = createAnalyticsAtom("marketingActivity");
export const metaMarketingActivityAtom = createAnalyticsAtom(
  "metaMarketingActivity",
  {
    queryVariables: {
      marketingIntegrationType: "META_ADS",
    },
  }
);
export const googleMarketingActivityAtom = createAnalyticsAtom(
  "googleMarketingActivity",
  {
    queryVariables: {
      marketingIntegrationType: "GOOGLE_ADS",
    },
  }
);

/**
 * Load a report into its shared atom, scoped to the selected store and currency.
 * Stale data is hidden during render, and responses are accepted only by the
 * request and store scope that started them, including a switch away and back.
 * The returned numberFormat belongs to the same store as the exposed report.
 */
export const useAnalytics = (
  atom,
  query,
  queryFieldName,
  dataParser = (d) => d
) => {
  const { selectedIntegration } = useIntegration(storeIntegrationTypes.shopify);
  const [stored, setStore] = useAtom(atom);
  const selectedInterval = useAtomValue(reportingIntervalAtom);
  const supportsInterval = query?.definitions?.some((definition) =>
    definition.variableDefinitions?.some(
      ({ variable }) => variable.name.value === "reportingInterval"
    )
  );
  const reportingInterval = supportsInterval ? selectedInterval : "auto";
  const numberFormat = useNumberFormat(selectedIntegration?.currency);
  const scopeKey = `${selectedIntegration?.id || ""}:${numberFormat.currencyCode}:${reportingInterval}`;
  const scopeRef = useRef({ key: scopeKey });
  if (scopeRef.current.key !== scopeKey) scopeRef.current = { key: scopeKey };
  const currentScope = scopeRef.current;
  const ownsStore =
    stored.selectedIntegrationId === selectedIntegration?.id &&
    stored.currency === numberFormat.currencyCode;
  const ownsData =
    ownsStore &&
    (stored.requestedReportingInterval || "auto") === reportingInterval;
  // Mask stale data during render, before effects can reset the shared atom.
  const store = ownsData
    ? stored
    : {
        ...(ownsStore ? stored : stored.defaultState),
        data: [],
        periods: [],
        loaded: false,
        defaultState: stored.defaultState,
        loading: false,
      };
  const safeWrapper = useSafeWrapper();
  const { queryApi } = useApiQuery({ query });

  const refreshVersion =
    selectedIntegration?.analyticsStatus?.lastCompletedAt || "";
  const requestedRefreshRef = useRef(null);
  useEffect(() => {
    const key = `${scopeKey}:${refreshVersion}`;
    // If a report request is in flight, keep the new version pending until it
    // finishes. Otherwise a completion observed during loading would be lost.
    if (
      !selectedIntegration ||
      store.loading ||
      requestedRefreshRef.current === key
    )
      return;
    requestedRefreshRef.current = key;
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, refreshVersion, store.loading]);

  const refreshData = safeWrapper(async () => {
    if (
      !selectedIntegration ||
      !store.dateFrom ||
      !store.dateTo ||
      store.loading
    ) {
      return;
    }

    const requestToken = {};
    setStore((previous) => ({
      ...(ownsStore
        ? previous
        : { ...previous.defaultState, defaultState: previous.defaultState }),
      selectedIntegrationId: selectedIntegration.id,
      currency: numberFormat.currencyCode,
      requestToken,
      requestedReportingInterval: reportingInterval,
      ...(!ownsData ? { data: [], periods: [], loaded: false } : {}),
      loading: true,
    }));

    try {
      const { compareFrom, compareTo } = getComparisonDates(
        store.dateFrom,
        store.dateTo,
        store.comparisonMode,
        store.dateMin,
        store.dateMax
      );

      const { data: response, error } = await queryApi({
        integrationId: selectedIntegration.id,
        dateFrom: store.dateFrom.toLocaleDateString("en-CA"),
        dateTo: store.dateTo.toLocaleDateString("en-CA"),
        compareFrom: compareFrom?.toLocaleDateString("en-CA"),
        compareTo: compareTo?.toLocaleDateString("en-CA"),
        ...(store.queryVariables || {}),
        ...(supportsInterval
          ? {
              reportingInterval:
                reportingInterval === "auto" ? null : reportingInterval,
            }
          : {}),
      });

      if (error || !response) {
        return;
      }

      let {
        [queryFieldName]: { data, minDate, resolution, __typename, ...rest },
      } = response;

      const dateMin = new Date(minDate);
      let dateFrom = store.dateFrom;
      if (dateFrom < dateMin) {
        dateFrom = dateMin;
      }

      const betweenFn =
        resolution === "month"
          ? getMonthsBetween
          : resolution === "week"
            ? getWeeksBetween
            : getDaysBetween;
      let periods = betweenFn(dateFrom, store.dateTo);

      // cleanup the periods to ensure they don't start or end with a period that is not in the data
      if (periods.length > 0 && Array.isArray(data) && data[0]?.period) {
        // Remove the first and last periods if they are not present in the data
        const [firstPeriod, ...rest] = periods;
        if (!data.some((d) => d.period === firstPeriod)) {
          periods = rest;
        }

        // Remove the last period if it is not present in the data
        const lastPeriod = periods[periods.length - 1];
        if (!data.some((d) => d.period === lastPeriod)) {
          periods = periods.slice(0, -1);
        }
      }

      data = dataParser({ data, periods, dateMin, ...rest });

      // update the store making sure to use current state which might have been
      // updated while the api was loading
      setStore((store) => {
        if (
          scopeRef.current !== currentScope ||
          store.requestToken !== requestToken ||
          store.selectedIntegrationId !== selectedIntegration.id
        ) {
          // integration changed while we were loading
          return store;
        }

        const effectiveComparisonMode =
          store.comparisonMode === "noComparison"
            ? "noComparison"
            : getComparisonDates(
                  dateFrom,
                  store.dateTo,
                  store.comparisonMode,
                  dateMin,
                  store.dateMax
                ).compareFrom
              ? store.comparisonMode
              : "noComparison";

        return {
          ...store,
          ...rest,
          periods,
          data,
          dateMin,
          resolution,
          dateFrom,
          comparisonMode: effectiveComparisonMode,
          loaded: true,
        };
      });
    } finally {
      setStore((previous) =>
        previous.requestToken === requestToken &&
        scopeRef.current === currentScope
          ? { ...previous, loading: false }
          : previous
      );
    }
  });

  return {
    atom,
    store: ownsData ? store : { ...store, loading: true },
    setStore,
    selectedIntegration,
    numberFormat,
    refreshData,
    loading: !ownsData || store.loading,
  };
};
